import pako from "pako";
import axios from "../httpServices/httpService";
import decodeJson from "./decodeResponse";
import config from "../config.json";

/**
 * Batched requests for the live boards (card view, andon).
 *
 * The server computes all the machines of one request together - one shared load of
 * the shift's machine states and EquipmentParams, several machines at once - so a
 * request per machine wastes most of that work. LiveBatchSize in config.json sets how
 * many machines go in one request.
 */
export const LIVE_BATCH_SIZE = Math.max(1, Number(config.LiveBatchSize) || 1);

function putEquips(url, userId, equips) {
  const body = pako.gzip(JSON.stringify({ UserId: userId, Equips: equips }));
  return axios
    .put(url, body, {
      headers: {
        "Content-Encoding": "gzip",
        "Content-Type": "application/json",
      },
    })
    .then((res) => decodeJson(res.data));
}

const isServerError = (err) => !!(err && err.response && err.response.status >= 500);

/**
 * Requests `equips` LIVE_BATCH_SIZE machines at a time, one request after another, and
 * passes each answer's rows to onRows as it arrives.
 *
 * When the server fails a multi-machine request, its machines are asked for one at a
 * time, so a machine the server cannot compute costs only its own row - as it did when
 * every request carried one machine. onError is called once, with the first failure.
 * Stops early once isActive() returns false.
 */
export async function fetchLiveBatches({ url, userId, equips, onRows, onError, isActive = () => true }) {
  let reported = false;
  const report = (err) => {
    if (reported) return;
    reported = true;
    if (onError) onError(err);
  };

  for (let start = 0; start < equips.length; start += LIVE_BATCH_SIZE) {
    if (!isActive()) return;
    const batch = equips.slice(start, start + LIVE_BATCH_SIZE);
    try {
      onRows(await putEquips(url, userId, batch));
    } catch (err) {
      if (batch.length === 1 || !isServerError(err)) {
        report(err);
        continue;
      }
      for (const equip of batch) {
        if (!isActive()) return;
        try {
          onRows(await putEquips(url, userId, [equip]));
        } catch (singleErr) {
          report(singleErr);
        }
      }
    }
  }
}

/**
 * One row per machine, in the order of `equips`: the machine's row from freshRows, or
 * its row from previousRows when this refresh brought none (its request failed).
 * Machines no longer in `equips` are dropped. Rows are matched on row.Equipment, the
 * machine's EquipmentID.
 */
export function mergeRowsByEquipment(equips, previousRows, freshRows) {
  const byEquipment = new Map();
  (previousRows || []).forEach((row) => row && byEquipment.set(row.Equipment, row));
  (freshRows || []).forEach((row) => row && byEquipment.set(row.Equipment, row));
  return equips.map((equip) => byEquipment.get(equip.EquipmentID)).filter(Boolean);
}
